import { TranslatableTextProps } from "@components/translations/TranslatableText";
import type { LandUseChangeResponse } from "../pages/activityBuilder/modules/landUseChanges/landUseChangeTypes";
import type { Activity, ModuleStatus, Project } from "../types/interfaces";
import { FetchOptionsKit, TranslationKey } from "../types/modulesInterfaces";
import { ModuleTypeRegistry } from "./moduleTypeRegistry";
import { getOptions } from "@app/features/dropdownOptions/fetchOptions";

export enum FEModules {
	PerennialCropland = 1,
	AnnualCropland = 2,
	LivestockManagement = 3,
	Energy = 4,
	Irrigation = 5,
	Inputs = 6,
	FloodedRice = 7,
	ForestManagement = 8,
	LandUseChange = 9,
	LargeFisheries = 10,
	SmallFisheries = 11,
	Aquaculture = 12,
	OrganicSoil = 13,
	CoastalWetlands = 14,
	Waterbodies = 15,
	SetAside = 16,
	OtherLand = 17,
	Settlements = 18,
	Grassland = 19,
	Packaging = 21,
	Transport = 22,
	Storage = 23,
	Processing = 24,
	ActivityComplete = 20,
}

export const MODULES_WITH_LAND_USE = [
	FEModules.PerennialCropland,
	FEModules.AnnualCropland,
	FEModules.FloodedRice,
	FEModules.ForestManagement,
	FEModules.Grassland,
	FEModules.SetAside,
	FEModules.OtherLand,
	FEModules.Settlements,
	// FEModules.CoastalWetlands, 
];

export const isInFEModules = (num: number) =>
	Object.values(FEModules).includes(num);

// update this enum with the class_name string in the backend
export enum BEModulesClassNames {
	ActivityComplete = "ActivityComplete",
	PerennialCropland = "PerennialCropland",
	AnnualCropland = "AnnualCropland",
	LivestockManagement = "Livestock",
	Energy = "Energy",
	Irrigation = "Irrigation",
	IrrigationSystem = "IrrigationSystem",
	OperationPhaseOfIrrigation = "IrrigationPhase",
	Inputs = "Input",
	FloodedRice = "FloodedRice",
	ForestManagement = "ForestManagement",
	LandUseChange = "LandUseChange",
	LargeFisheries = "LargeFishery",
	SmallFisheries = "SmallFishery",
	Aquaculture = "Aquaculture",
	OrganicSoil = "OrganicSoil",
	CoastalWetlands = "CoastalWetland",
	Waterbodies = "Waterbody",
	SetAside = "SetAside",
	OtherLand = "OtherLand",
	Settlements = "Settlement",
	Road = "Road",
	Building = "Building",
	OtherInfrastructure = "OtherInfrastructure",
	Grassland = "Grassland",
	Packaging = "Packaging",
	Transport = "Transport",
	Storage = "Storage",
	Processing = "Processing",
}



export type FE2BEModuleObj = {
	[key in FEModules]: {
		name: TranslationKey;
		path: string;
		youtubeId?: string;
		beModuleClassName: BEModulesClassNames;
	};
};

export const FEModulesObj: FE2BEModuleObj = {
	[FEModules.LandUseChange]: {
		name: "project.land_use_change",
		beModuleClassName: BEModulesClassNames.LandUseChange,
		path: "land-use-changes",
		youtubeId: "-wP27daIxUM"
	},
	[FEModules.AnnualCropland]: {
		name: "project.annual_cropland",
		beModuleClassName: BEModulesClassNames.AnnualCropland,
		path: "annual-croplands",
		youtubeId: "PKp37s5ZwqA"
	},
	[FEModules.PerennialCropland]: {
		name: "project.perennial_cropland",
		beModuleClassName: BEModulesClassNames.PerennialCropland,
		path: "perennial-croplands",
		youtubeId: "lPwHcKWE3XA"
	},
	[FEModules.FloodedRice]: {
		name: "project.flooded_rice",
		beModuleClassName: BEModulesClassNames.FloodedRice,
		path: "flooded-rices",
		youtubeId: "_P_vO9GcFrk"
	},
	[FEModules.SetAside]: {
		name: "project.set_aside",
		beModuleClassName: BEModulesClassNames.SetAside,
		path: "set-asides",
	},
	[FEModules.Grassland]: {
		name: "project.grassland",
		beModuleClassName: BEModulesClassNames.Grassland,
		path: "grasslands",
		youtubeId: "8wCRN8uylQc"
	},
	[FEModules.ForestManagement]: {
		name: "project.forest_management",
		beModuleClassName: BEModulesClassNames.ForestManagement,
		path: "forest-managements",
		youtubeId: "jGhTfg8sj9U"
	},
	[FEModules.Settlements]: {
		name: "project.settlement",
		beModuleClassName: BEModulesClassNames.Settlements,
		path: "settlements",
		youtubeId: "b4nwHS8VyLE"
	},
	[FEModules.OtherLand]: {
		name: "project.other_land",
		beModuleClassName: BEModulesClassNames.OtherLand,
		path: "other-lands",
	},
	[FEModules.OrganicSoil]: {
		name: "project.organic_soil",
		beModuleClassName: BEModulesClassNames.OrganicSoil,
		path: "organic-soils",
		youtubeId: "R_yAj7vbP44"
	},
	[FEModules.CoastalWetlands]: {
		name: "project.coastal_wetlands",
		beModuleClassName: BEModulesClassNames.CoastalWetlands,
		path: "coastal-wetlands",
	},
	[FEModules.Waterbodies]: {
		name: "project.waterbodies",
		beModuleClassName: BEModulesClassNames.Waterbodies,
		path: "waterbodies",
	},
	[FEModules.LivestockManagement]: {
		name: "project.livestock_management",
		beModuleClassName: BEModulesClassNames.LivestockManagement,
		path: "livestocks",
		youtubeId: "Uz9atHjEI48"
	},
	[FEModules.SmallFisheries]: {
		name: "project.small_fisheries",
		beModuleClassName: BEModulesClassNames.SmallFisheries,
		path: "small-fisheries",
		youtubeId: "161zksZ-cMQ"
	},
	[FEModules.LargeFisheries]: {
		name: "project.large_fisheries",
		beModuleClassName: BEModulesClassNames.LargeFisheries,
		path: "large-fisheries",
		youtubeId: "161zksZ-cMQ"
	},
	[FEModules.Aquaculture]: {
		name: "project.aquaculture",
		beModuleClassName: BEModulesClassNames.Aquaculture,
		path: "aquacultures",
		youtubeId: "kg7peSJRF18"
	},
	[FEModules.Inputs]: {
		name: "project.inputs",
		beModuleClassName: BEModulesClassNames.Inputs,
		path: "inputs",
		youtubeId: "0Ehahd9OgkQ"
	},
	[FEModules.Energy]: {
		name: "project.energy",
		beModuleClassName: BEModulesClassNames.Energy,
		path: "energies",
		youtubeId: "0Ehahd9OgkQ"
	},
	[FEModules.Irrigation]: {
		name: "project.irrigation",
		beModuleClassName: BEModulesClassNames.Irrigation,
		path: "irrigations",
		youtubeId: "0Ehahd9OgkQ"
	},
	[FEModules.Packaging]: {
		name: "project.packaging",
		beModuleClassName: BEModulesClassNames.Packaging,
		path: "packagings",
		youtubeId: "OiL6hms2jtw"
	},
	[FEModules.Transport]: {
		name: "project.transport",
		beModuleClassName: BEModulesClassNames.Transport,
		path: "transports",
		youtubeId: "OiL6hms2jtw"

	},
	[FEModules.Storage]: {
		name: "project.storage",
		beModuleClassName: BEModulesClassNames.Storage,
		path: "storages",
		youtubeId: "OiL6hms2jtw"

	},
	[FEModules.Processing]: {
		name: "project.processing",
		beModuleClassName: BEModulesClassNames.Processing,
		path: "processings",
		youtubeId: "OiL6hms2jtw"
	},
	[FEModules.ActivityComplete]: {
		name: "project.activity_complete",
		beModuleClassName: BEModulesClassNames.ActivityComplete,
		path: "complete",
	},
};


export interface FeActivityModule {
	id: FEModules;
	name: string;
	path: string;
	uniqueId?: number; //this id refers to the unique id that the specific module has
}

interface Mod {
	beModuleClassName: BEModulesClassNames,
	uniqueId: number,
	status: ModuleStatus,
}

// Helper function to create BE to FE module mapping
const createBeToUIDModuleMapping = (mods: Mod[]) => {
	const beModules = mods.map(el => el.beModuleClassName);
	const ids = mods.map(el => el.uniqueId);

	return beModules.reduce((acc, mod, i) => {
		acc[mod] = ids[i];
		return acc;
	}, {} as Record<BEModulesClassNames, number>);
};

// Helper function to determine module category
const getModuleCategory = (
	module: BEModulesClassNames,
	modulesToMiddle: BEModulesClassNames[],
	modulesToEnd: BEModulesClassNames[]
): number => {
	if (modulesToEnd.includes(module)) return 2;
	if (modulesToMiddle.includes(module)) return 1;
	return 0;
};

// Helper function to check if module is LUC-related
const isLUCRelated = (
	module: number,
	lucInfo?: {
		start: number | null;
		without: number | null;
		with: number | null;
	}
): boolean => {
	if (!lucInfo) return false;
	const moduleRegistry = ModuleTypeRegistry.getInstance();
	const lucId = moduleRegistry.getIdByClassName(BEModulesClassNames.LandUseChange);
	return (
		module === lucId ||
		module === lucInfo.start ||
		module === lucInfo.without ||
		module === lucInfo.with
	);
};

// Helper function to compare LUC-related modules
const compareLUCModules = (
	x: number,
	y: number,
	lucInfo: {
		start: number | null;
		without: number | null;
		with: number | null;
	}
): number => {
	const landUseChangeId = ModuleTypeRegistry.getInstance().getIdByClassName(BEModulesClassNames.LandUseChange);

	// Sort order: start -> LUC -> without -> with
	if (x === lucInfo.start) return -1;
	if (y === lucInfo.start) return 1;
	if (x === landUseChangeId) return -1;
	if (y === landUseChangeId) return 1;
	if (x === lucInfo.without) return -1;
	if (y === lucInfo.without) return 1;
	if (x === lucInfo.with) return -1;
	if (y === lucInfo.with) return 1;
	return 0;
};

// Helper function to determine module position within category
const getModulePosition = (
	module: BEModulesClassNames,
	modulesToMiddle: BEModulesClassNames[],
	modulesToEnd: BEModulesClassNames[]
): number => {
	const middleIndex = modulesToMiddle.indexOf(module);
	if (middleIndex !== -1) return middleIndex;

	const endIndex = modulesToEnd.indexOf(module);
	if (endIndex !== -1) return endIndex;

	return 0;
};

// Main sorting function that handles both cases
const sortModules = (
	modules: BEModulesClassNames[],
	lucInfo?: {
		start: number | null;
		without: number | null;
		with: number | null;
	}
) => {
	const moduleRegistry = ModuleTypeRegistry.getInstance();
	const sorted = [...modules];
	const modulesToMiddle = [BEModulesClassNames.Inputs, BEModulesClassNames.Energy, BEModulesClassNames.Irrigation];
	const modulesToEnd = [BEModulesClassNames.Transport, BEModulesClassNames.Processing, BEModulesClassNames.Packaging, BEModulesClassNames.Storage];

	const sortedModules = sorted.filter(mod => mod !== undefined).sort((x, y) => {
		// Handle LUC-related sorting if lucInfo is provided
		const xId = moduleRegistry.getIdByClassName(x) as number;
		const yId = moduleRegistry.getIdByClassName(y) as number;
		const xIsLUCRelated = isLUCRelated(xId, lucInfo);
		const yIsLUCRelated = isLUCRelated(yId, lucInfo);

		if (lucInfo && (xIsLUCRelated || yIsLUCRelated)) {
			return compareLUCModules(xId, yId, lucInfo);
		}

		// Handle category-based sorting
		const xCategory = getModuleCategory(x, modulesToMiddle, modulesToEnd);
		const yCategory = getModuleCategory(y, modulesToMiddle, modulesToEnd);

		// If categories are different, sort by category
		if (xCategory !== yCategory) {
			return xCategory - yCategory;
		}

		// If in same category, sort by position within category array
		return getModulePosition(x, modulesToMiddle, modulesToEnd) -
			getModulePosition(y, modulesToMiddle, modulesToEnd);
	});

	return sortedModules;
};

// Main function refactored
export const generateActivityModuleList = (
	mods: Mod[],
	lucInfo?: {
		start: number | null;
		without: number | null;
		with: number | null;
	},
) => {
	const beModules = mods.map(el => el.beModuleClassName);
	const moduleMapping = createBeToUIDModuleMapping(mods);

	// Sort modules based on whether LUC is present
	const sortedModules = sortModules(beModules, lucInfo)

	// Map BE modules to FE modules
	const feMods = sortedModules.map(beMod => {
		const correctBeMod = mods.find(mod => mod.beModuleClassName === beMod);
		return {
			mod: BEModulesObj[beMod],
			id: lucInfo ? moduleMapping[beMod] : correctBeMod?.uniqueId,
			status: correctBeMod?.status,
		};
	});

	// Create final module list
	const moduleList = new Set<FeActivityModule>();
	for (const mod of feMods) {
		moduleList.add({
			id: mod.mod,
			name: FEModulesObj[mod.mod].name,
			path: FEModulesObj[mod.mod].path ?? "",
			uniqueId: mod.id,
		});
	}

	return Array.from(moduleList);
};

//in realtà questo sarà il nuovo ModuleStep
export type BE2FEModulesObj = {
	[key in BEModulesClassNames]: FEModules;
};

export const BEModulesObj: BE2FEModulesObj = {
	[BEModulesClassNames.AnnualCropland]: FEModules.AnnualCropland,
	[BEModulesClassNames.SmallFisheries]: FEModules.SmallFisheries,
	[BEModulesClassNames.LargeFisheries]: FEModules.LargeFisheries,
	[BEModulesClassNames.PerennialCropland]: FEModules.PerennialCropland,
	[BEModulesClassNames.LivestockManagement]: FEModules.LivestockManagement,
	[BEModulesClassNames.Energy]: FEModules.Energy,
	[BEModulesClassNames.Irrigation]: FEModules.Irrigation,
	[BEModulesClassNames.IrrigationSystem]: FEModules.Irrigation,
	[BEModulesClassNames.OperationPhaseOfIrrigation]: FEModules.Irrigation,
	[BEModulesClassNames.FloodedRice]: FEModules.FloodedRice,
	[BEModulesClassNames.ForestManagement]: FEModules.ForestManagement,
	[BEModulesClassNames.LandUseChange]: FEModules.LandUseChange,
	[BEModulesClassNames.Aquaculture]: FEModules.Aquaculture,
	[BEModulesClassNames.OrganicSoil]: FEModules.OrganicSoil,
	[BEModulesClassNames.CoastalWetlands]: FEModules.CoastalWetlands,
	[BEModulesClassNames.Waterbodies]: FEModules.Waterbodies,
	[BEModulesClassNames.SetAside]: FEModules.SetAside,
	[BEModulesClassNames.OtherLand]: FEModules.OtherLand,
	[BEModulesClassNames.Settlements]: FEModules.Settlements,
	[BEModulesClassNames.Road]: FEModules.Settlements,
	[BEModulesClassNames.Building]: FEModules.Settlements,
	[BEModulesClassNames.OtherInfrastructure]: FEModules.Settlements,
	[BEModulesClassNames.Grassland]: FEModules.Grassland,
	[BEModulesClassNames.Inputs]: FEModules.Inputs,
	[BEModulesClassNames.Packaging]: FEModules.Packaging,
	[BEModulesClassNames.Transport]: FEModules.Transport,
	[BEModulesClassNames.Storage]: FEModules.Storage,
	[BEModulesClassNames.Processing]: FEModules.Processing,
	[BEModulesClassNames.ActivityComplete]: FEModules.ActivityComplete,
};

export const activityCompleteInfo: FeActivityModule = {
	id: FEModules.ActivityComplete,
	name: FEModulesObj[FEModules.ActivityComplete].name,
	path: FEModulesObj[FEModules.ActivityComplete].path ?? "",
};

type FEModuleDescription = {
	[key in FEModules]: TranslatableTextProps;
};

export const moduleDescriptions: FEModuleDescription = {
	[FEModules.PerennialCropland]: { translationKey: "activity.perennialcropland_description" },
	[FEModules.AnnualCropland]: { translationKey: "activity.annualcropland_description" },
	[FEModules.LivestockManagement]: { translationKey: "activity.livestockmanagement_description" },
	[FEModules.Energy]: { translationKey: "activity.energy_description" },
	[FEModules.Irrigation]: {
		translationKey: "activity.irrigation_description",
		paramsComponents: [<a href="https://aquastat.fao.org/climate-information-tool/" style={{ color: "var(--primary-bright)", textDecoration: "underline" }} target="_blank" rel="noopener noreferrer" />]
	},
	[FEModules.FloodedRice]: { translationKey: "activity.floodedrice_description" },
	[FEModules.ForestManagement]: { translationKey: "activity.forestmanagement_description" },
	[FEModules.LandUseChange]: { translationKey: "activity.landusechange_description" },
	[FEModules.LargeFisheries]: { translationKey: "activity.largefisheries_description" },
	[FEModules.SmallFisheries]: { translationKey: "activity.smallfisheries_description" },
	[FEModules.Aquaculture]: { translationKey: "activity.aquaculture_description" },
	[FEModules.OrganicSoil]: { translationKey: "activity.organicsoil_description" },
	[FEModules.CoastalWetlands]: { translationKey: "activity.coastalwetlands_description" },
	[FEModules.Waterbodies]: { translationKey: "activity.waterbodies_description" },
	[FEModules.SetAside]: { translationKey: "activity.setaside_description" },
	[FEModules.OtherLand]: { translationKey: "activity.otherland_description" },
	[FEModules.Settlements]: { translationKey: "activity.settlements_description" },
	[FEModules.Grassland]: { translationKey: "activity.grassland_description" },
	[FEModules.Inputs]: {
		translationKey: "activity.inputs_description",
		paramsComponents: [<a href="https://www.ifastat.org/converter/fertilizer-converter/" style={{ color: "var(--primary-bright)", textDecoration: "underline" }} target="_blank" rel="noopener noreferrer" />]
	},
	[FEModules.Packaging]: { translationKey: "activity.packaging_description" },
	[FEModules.Transport]: { translationKey: "activity.transport_description" },
	[FEModules.Storage]: { translationKey: "activity.storage_description" },
	[FEModules.Processing]: { translationKey: "activity.processing_description" },
	[FEModules.ActivityComplete]: { translationKey: "" }
};

export const getFEModuleListFromActivity = (activity: Activity) => {
	const landUseChangeId = ModuleTypeRegistry.getInstance().getIdByClassName(BEModulesClassNames.LandUseChange);
	const luc = activity.modules?.find(
		(mod) => mod.module_type?.id === landUseChangeId,
	) as LandUseChangeResponse | undefined;
	// const beModuleIds = activity.module_types.map((mod) => mod.id);
	const beMods: Mod[] = activity.modules?.map((mod) => {
		return {
			uniqueId: mod.id,
			beModuleClassName: mod.module_type.class_name as BEModulesClassNames,
			status: mod.status,
		};
	});

	const feModuleList = generateActivityModuleList(
		beMods,
		luc
			? {
				start: luc.module_type_start,
				with: luc.module_type_w,
				without: luc.module_type_wo,
			}
			: undefined
	);

	return feModuleList;
}

export function getModuleFromUrl(url: string) {
	const sections = url.split('/');
	const section = sections[sections.length - 1];
	return getModulefromModulePathName(section);
}
export function isLandUseModule(module: BEModulesClassNames | undefined) {
	if (!module) return false;
	return MODULES_WITH_LAND_USE.includes(BEModulesObj[module]);
}
export function getModulefromModulePathName(modulePathName: string) {
	return Object.values(FEModulesObj).find((module) => module.path === modulePathName)
}

export function getModuleNameFromBEId(beModuleId: number) {
	const className = ModuleTypeRegistry.getInstance().getClassNameById(beModuleId);
	if (!className) return undefined;
	const frontId = BEModulesObj[className as keyof typeof BEModulesObj]
	return FEModulesObj[frontId]?.name ?? ""
}

export function getLandUseModuleIdFromActivity(modules: FeActivityModule[]): number | undefined {
	return modules.find((mod) => MODULES_WITH_LAND_USE.includes(mod.id))?.uniqueId;
}
export function getAreaFromLUCModule(activity: Activity | null | undefined, moduleList: FeActivityModule[]) {
	if (!activity) return null
	const landUseModuleInActivityId = getLandUseModuleIdFromActivity(moduleList)
	if (!landUseModuleInActivityId) return null
	const landUseModule = activity?.modules?.find((mod) => mod.id === landUseModuleInActivityId)
	return landUseModule?.area ?? null
}



// New function that accepts class name and resolves to current ID
export const getModuleIdByClassName = (className: BEModulesClassNames): number | undefined => {
	const registry = ModuleTypeRegistry.getInstance();
	return registry.getIdByClassName(className);
};

// Updated filtering function that supports both class names and IDs
export const fetchLandUseTypesByClassName = ({
	className,
	activity,
	project
}: {
	className?: BEModulesClassNames;
	activity?: Activity | null | undefined;
	project?: Project | null | undefined;
}): () => Promise<FetchOptionsKit> => {
	const climate = activity?.climate_t2?.id || project?.climate.id;
	const moisture = activity?.moisture_t2?.id || project?.moisture.id;

	// Resolve class name to current ID
	const currentId = className ? getModuleIdByClassName(className) : undefined;

	return () => getOptions("landUseType", {
		filterId: currentId,
		climate: climate ?? undefined,
		moisture: moisture ?? undefined
	});
};

// Hybrid function that accepts both beModuleId and className
export const fetchLandUseTypesHybrid = ({
	beModuleId,
	className,
	activity,
	project
}: {
	beModuleId?: number;
	className?: BEModulesClassNames;
	activity?: Activity | null | undefined;
	project?: Project | null | undefined;
}): () => Promise<FetchOptionsKit> => {
	const climate = activity?.climate_t2?.id || project?.climate.id;
	const moisture = activity?.moisture_t2?.id || project?.moisture.id;

	// Use class name if provided, otherwise fall back to beModuleId
	const filterId = className ? getModuleIdByClassName(className) : beModuleId;

	return () => getOptions("landUseType", {
		filterId,
		climate: climate ?? undefined,
		moisture: moisture ?? undefined
	});
};

// Helper function to check if module is LUC by class name
export const isLucModuleByClassName = (className: BEModulesClassNames): boolean => {
	const registry = ModuleTypeRegistry.getInstance();
	return registry.isLucModule(className);
};

// Helper function to get module info by class name
export const getModuleInfoByClassName = (className: BEModulesClassNames) => {
	const registry = ModuleTypeRegistry.getInstance();
	return registry.getModuleInfoByClassName(className);
};

// Update the MODULES_WITH_LAND_USE array to use class names
export const MODULES_WITH_LAND_USE_CLASS_NAMES = [
	BEModulesClassNames.PerennialCropland,
	BEModulesClassNames.AnnualCropland,
	BEModulesClassNames.FloodedRice,
	BEModulesClassNames.ForestManagement,
	BEModulesClassNames.Grassland,
	BEModulesClassNames.SetAside,
	BEModulesClassNames.OtherLand,
	BEModulesClassNames.Settlements,
	// BEModulesClassNames.CoastalWetlands, 
];

// New function to check if module is LUC by class name
export function isLandUseModuleByClassName(className: BEModulesClassNames) {
	return MODULES_WITH_LAND_USE_CLASS_NAMES.includes(className);
}